import { Flex, FlexProps, IconButton, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { getRealType, isNullable } from '@pelicargo/utils';
import { startCase } from 'lodash';
import { JSX } from 'react';
import { FieldValues, useFormContext, UseFormSetValue } from 'react-hook-form';
import { ZodArrayDef, ZodEnumDef, ZodNativeEnumDef, ZodObjectDef, ZodRawShape } from 'zod';

import { SimpleCheckbox } from './SimpleCheckbox';
import { SimpleInput } from './SimpleInput';
import { SimpleNumber } from './SimpleNumber';
import { SimpleReactSelect } from './SimpleReactSelect';
import { SimpleSelect } from './SimpleSelect';

export type DynamicOverrides<T extends ZodRawShape> = {
  [id in keyof T]?: JSX.Element;
};

const renderInput = <T extends ZodRawShape>(
  shape: T,
  attr: keyof T,
  overrides: DynamicOverrides<T>,
  hiddenOverrides: (keyof T)[],
): {
  element: JSX.Element;
  isNullable: boolean;
} => {
  const [name, def] = getRealType(shape[attr]);
  const attrName = attr as string;
  const label = startCase(attrName);
  if (attr in overrides) {
    return {
      element: overrides[attr],
      isNullable: !hiddenOverrides.includes(attr),
    };
  }

  const baseParams = {
    label,
    name: attrName,
    isRequired: !isNullable(shape[attr]),
  };

  switch (name) {
    case 'ZodString':
      return {
        element: <SimpleInput key={attrName} {...baseParams} />,
        isNullable: isNullable(shape[attr]),
      };
    case 'ZodNumber':
      return {
        element: <SimpleNumber key={attrName} {...baseParams} />,
        isNullable: true,
      };
    case 'ZodNativeEnum':
      return {
        element: (
          <SimpleSelect
            key={attrName}
            {...baseParams}
            options={Object.keys((def as ZodNativeEnumDef).values).map((value) => ({
              label: startCase(value),
              value,
            }))}
          />
        ),
        isNullable: true,
      };

    case 'ZodEnum':
      return {
        element: (
          <SimpleSelect
            key={attrName}
            {...baseParams}
            options={(def as ZodEnumDef).values.map((value) => ({
              label: startCase(value),
              value,
            }))}
          />
        ),
        isNullable: isNullable(shape[attr]),
      };

    case 'ZodBoolean':
      return {
        element: <SimpleCheckbox key={attrName} {...baseParams} />,
        isNullable: false,
      };
    case 'ZodArray': {
      const [arrayName, arrayDef] = getRealType((def as ZodArrayDef).type);
      switch (arrayName) {
        case 'ZodNativeEnum':
          return {
            element: (
              <SimpleReactSelect
                key={attrName}
                {...baseParams}
                isMulti
                options={Object.keys((arrayDef as ZodNativeEnumDef).values).map((value) => ({
                  label: startCase(value),
                  value,
                }))}
              />
            ),
            isNullable: false,
          };
        default:
          return {
            element: (
              <Text key={attrName} color="red">
                [Unsupported Field]
              </Text>
            ),
            isNullable: false,
          };
      }
    }
    default:
      return {
        element: (
          <Text color="red" key={attrName}>
            [Unsupported Field: {attrName} ({name})]
          </Text>
        ),
        isNullable: false,
      };
  }
};

const generateForm = <T extends ZodRawShape>(
  def: ZodObjectDef<T>,
  overrides: DynamicOverrides<T>,
  hiddenOverrides: (keyof T)[],
  setValue: UseFormSetValue<FieldValues>,
) => {
  const shape = def.shape();
  return Object.keys(shape).map((attr) => {
    const { element, isNullable } = renderInput(shape, attr, overrides, hiddenOverrides);

    return (
      <Flex key={attr} alignItems="last baseline" gap={2}>
        {element}
        <Show if={isNullable}>
          <IconButton
            aria-label="Clear value"
            icon={<Text>∅</Text>}
            colorScheme="red"
            onClick={() => {
              // shouldnt be necessary but to be safe
              if (getRealType(shape[attr])[0] === 'ZodArray') {
                setValue(attr, []);
              } else {
                setValue(attr, null);
              }
            }}
          />
        </Show>
      </Flex>
    );
  });
};

type DynamicFormProps<T extends ZodRawShape> = {
  def: ZodObjectDef<T>;
  overrides: DynamicOverrides<T>;
  hiddenOverrides?: (keyof T)[];
  renderProps?: FlexProps;
};

/**
 * Generates a simple form based off of a zod schema
 * `def` can be obtained via `thing._def`
 * `overrides` is used for more complex fields (e.g. airport id search)
 * `hiddenOverrides` is used for rendering a field but not adding the data clear button (e.g. making a <input type="hidden" /> actually hidden)
 */
export const DynamicForm = <T extends ZodRawShape>({
  def,
  overrides,
  hiddenOverrides = [],
  renderProps,
}: DynamicFormProps<T>) => {
  const methods = useFormContext();

  return (
    <Flex flexDir="column" gap={2} {...renderProps}>
      {generateForm(def, overrides, hiddenOverrides, methods.setValue)}
    </Flex>
  );
};
